import { Pagination } from '@mui/material';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { CiEdit } from "react-icons/ci";
import { MdDeleteOutline } from 'react-icons/md';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { BACKEND_SERVER_URL } from '../../../http/http-config';
import '../common-css/Table.css';
import { deleteItem, showTableResponse, sortInAlphabeticalOrder, multiSortTopics } from '../common-helpers/commonServices';
import UGC_SubjectModal from './UGC_SubjectModal';
import UGC_SectionModal from './UGC_SectionModal';
import UGC_TopicModal from './UGC_TopicModal';
import UGC_QuestionBankModal from './UGC_QuestionBankModal';

const UGC_AllSubjects = () => {
    const subjectRef = useRef();
    const sectionRef = useRef();
    const topicRef = useRef();
    const questionModalRef = useRef();

    const [subjects, setSubjects] = useState([]);
    const [sections, setSections] = useState([]);
    const [topics, setTopics] = useState([]);

    const [selectedSubjects, setSelectedSubjects] = useState([]);
    const [selectedSections, setSelectedSections] = useState([]);
    const [selectedTopicsDropdown, setSelectedTopicsDropdown] = useState([]);

    const [selectedExamType, setSectedExamType] = useState(() => sessionStorage.getItem('ugc_selectedExamType') || "All Exam Types");
    const [selectedSubjectType, setSelectedSubjectType] = useState(() => {
        const saved = sessionStorage.getItem('ugc_selectedSubjectType');
        return saved ? JSON.parse(saved) : { Subject: "All SUBJECT NAME" };
    });
    const [selectedSection, setSelectedSection] = useState(() => {
        const saved = sessionStorage.getItem('ugc_selectedSection');
        return saved ? JSON.parse(saved) : { Section: "All TOPICS" };
    });
    const [selectedTopic, setSelectedTopic] = useState(() => {
        const saved = sessionStorage.getItem('ugc_selectedTopic');
        return saved ? JSON.parse(saved) : { Topic: "All SUB TOPICS" };
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [checked, setChecked] = useState([]);
    const [loadData, setLoadData] = useState(false);

    const getExamTypeLabel = (id) => {
        if (id === "COMBINED") return "COMBINED TESTS";
        if (id === "SUBJECT SPECIFIC") return "Academic Subjects";
        if (id === "GENERAL APTITUDE") return "Teaching & Research Aptitude";
        return id || '';
    };



    const sortDropdown = (array, labelKey) => {
        return array.sort((a, b) => {
            const orderA = a.DisplayOrder || 0;
            const orderB = b.DisplayOrder || 0;
            if (orderA > 0 && orderB === 0) return -1;
            if (orderA === 0 && orderB > 0) return 1;
            if (orderA > 0 && orderB > 0 && orderA !== orderB) {
                return orderA - orderB;
            }
            const labelA = a[labelKey]?.toLowerCase() || '';
            const labelB = b[labelKey]?.toLowerCase() || '';
            if (labelA < labelB) return -1;
            if (labelA > labelB) return 1;
            return 0;
        });
    };

    // Fetch all data
    useEffect(() => {
        const response = axios.all([
            axios.get(BACKEND_SERVER_URL + '/ugc/subjects'),
            axios.get(BACKEND_SERVER_URL + '/ugc/sections'),
            axios.get(BACKEND_SERVER_URL + '/ugc/topics')
        ]);

        response.then(axios.spread((subjectsResponse, sectionsResponse, topicsResponse) => {
            let fetchedSubjects = subjectsResponse.data.subjects;
            let fetchedSections = sectionsResponse.data.sections;
            let fetchedTopics = topicsResponse.data.topics;

            setSubjects(fetchedSubjects);
            setSections(sortDropdown(fetchedSections, 'Section'));
            setTopics(multiSortTopics(fetchedTopics));

            // Re-apply filters from storage
            const savedExam = sessionStorage.getItem('ugc_selectedExamType') || "All Exam Types";
            const savedSubject = sessionStorage.getItem('ugc_selectedSubjectType') ? JSON.parse(sessionStorage.getItem('ugc_selectedSubjectType')) : { Subject: "All SUBJECT NAME" };
            const savedSection = sessionStorage.getItem('ugc_selectedSection') ? JSON.parse(sessionStorage.getItem('ugc_selectedSection')) : { Section: "All TOPICS" };
            const savedTopic = sessionStorage.getItem('ugc_selectedTopic') ? JSON.parse(sessionStorage.getItem('ugc_selectedTopic')) : { Topic: "All SUB TOPICS" };

            let finalChecked = [...multiSortTopics(fetchedTopics)];

            if (savedExam !== 'All Exam Types') {
                let filteredSubjects = fetchedSubjects.filter(subject => subject.ExamType === savedExam);
                setSelectedSubjects(sortDropdown(filteredSubjects, 'Subject'));
                finalChecked = finalChecked.filter(topic => topic.ExamType === savedExam);
            } else {
                setSelectedSubjects([]);
            }

            if (savedSubject && savedSubject.Subject !== 'All SUBJECT NAME') {
                let filteredSections = fetchedSections.filter(sec => sec.SubjectID === savedSubject.Sn);
                setSelectedSections(sortDropdown(filteredSections, 'Section'));
                finalChecked = finalChecked.filter(topic => topic.SubjectID === savedSubject.Sn);
            } else {
                setSelectedSections([]);
            }

            if (savedSection && savedSection.Section !== 'All TOPICS') {
                let filteredTopicsList = fetchedTopics.filter(topic => topic.SectionID === savedSection.Sn);
                setSelectedTopicsDropdown(multiSortTopics(filteredTopicsList));
                finalChecked = finalChecked.filter(topic => topic.SectionID === savedSection.Sn);
            } else {
                setSelectedTopicsDropdown([]);
            }

            if (savedTopic && savedTopic.Topic !== 'All SUB TOPICS') {
                finalChecked = finalChecked.filter(topic => topic.Sn === savedTopic.Sn);
            }

            setChecked(finalChecked);
        })).catch((error) => {
            console.log(error);
            toast.error(error.response?.data?.message || "An error occurred while fetching data.");
        });

    }, [loadData])

    const pageSize = 100;
    const lastIndex = currentPage * pageSize;
    const firstIndex = lastIndex - pageSize;

    let currentSection = null;
    let sectionCounter = 0;
    const checkedWithSlNo = checked?.map((item) => {
        if (item.ExamType !== currentSection) {
            currentSection = item.ExamType;
            sectionCounter = 1;
        } else {
            sectionCounter++;
        }
        return { ...item, displaySlNo: sectionCounter };
    });

    const records = checkedWithSlNo?.slice(firstIndex, lastIndex);
    const npage = Math.ceil(checked?.length / pageSize)
    const number = [...Array(npage + 1).keys()].slice(1)

    const selectExamTypeHandler = (examType) => {
        setSelectedSubjectType({ Subject: "All SUBJECT NAME" });
        sessionStorage.setItem('ugc_selectedSubjectType', JSON.stringify({ Subject: "All SUBJECT NAME" }));
        setSelectedSection({ Section: "All TOPICS" });
        sessionStorage.setItem('ugc_selectedSection', JSON.stringify({ Section: "All TOPICS" }));
        setSectedExamType(examType);
        sessionStorage.setItem('ugc_selectedExamType', examType);

        if (examType === 'All Exam Types') {
            setChecked([...multiSortTopics(topics)]);
            setSelectedSubjects([]);
            setSelectedSections([]);
            setSelectedTopicsDropdown([]);
            setSelectedTopic({ Topic: "All SUB TOPICS" });
            sessionStorage.setItem('ugc_selectedTopic', JSON.stringify({ Topic: "All SUB TOPICS" }));
            return;
        }

        let filteredSubjects = subjects.filter(subject => subject.ExamType === examType);
        setSelectedSubjects(sortDropdown(filteredSubjects, 'Subject'));

        let filteredTopics = topics.filter(topic => topic.ExamType === examType);
        setChecked([...multiSortTopics(filteredTopics)]);
        setSelectedTopicsDropdown([]);
        setSelectedTopic({ Topic: "All SUB TOPICS" });
        sessionStorage.setItem('ugc_selectedTopic', JSON.stringify({ Topic: "All SUB TOPICS" }));
    }

    const selectSubjectTypeHandler = (subjectType) => {
        if (!subjectType || subjectType.Subject === 'All SUBJECT NAME') {
            let filteredTopics = topics.filter(topic => topic.ExamType === selectedExamType);
            setChecked(filteredTopics.length > 0 ? [...multiSortTopics(filteredTopics)] : [...multiSortTopics(topics)]);
            setSelectedSubjectType({ Subject: "All SUBJECT NAME", Sn: 0 });
            sessionStorage.setItem('ugc_selectedSubjectType', JSON.stringify({ Subject: "All SUBJECT NAME", Sn: 0 }));
            setSelectedSections([]);
            setSelectedSection({ Section: "All TOPICS", Sn: 0 });
            sessionStorage.setItem('ugc_selectedSection', JSON.stringify({ Section: "All TOPICS", Sn: 0 }));
            setSelectedTopicsDropdown([]);
            setSelectedTopic({ Topic: "All SUB TOPICS", Sn: 0 });
            sessionStorage.setItem('ugc_selectedTopic', JSON.stringify({ Topic: "All SUB TOPICS", Sn: 0 }));
            return;
        }

        setSelectedSubjectType(subjectType);
        sessionStorage.setItem('ugc_selectedSubjectType', JSON.stringify(subjectType));
        setSelectedSection({ Section: "All TOPICS", Sn: 0 });
        sessionStorage.setItem('ugc_selectedSection', JSON.stringify({ Section: "All TOPICS", Sn: 0 }));
        let filteredSections = sections.filter(sec => sec.SubjectID === subjectType.Sn);
        setSelectedSections(sortDropdown(filteredSections, 'Section'));

        let filteredTopics = topics.filter(topic => topic.SubjectID === subjectType.Sn);
        setChecked([...multiSortTopics(filteredTopics)]);
        setSelectedTopicsDropdown([]);
        setSelectedTopic({ Topic: "All SUB TOPICS", Sn: 0 });
        sessionStorage.setItem('ugc_selectedTopic', JSON.stringify({ Topic: "All SUB TOPICS", Sn: 0 }));
    }

    const selectSectionHandler = (section) => {
        if (!section || section.Section === 'All TOPICS') {
            let filteredTopics = topics.filter(topic => topic.SubjectID === selectedSubjectType.Sn);
            setChecked([...multiSortTopics(filteredTopics)]);
            setSelectedSection({ Section: "All TOPICS", Sn: 0 });
            sessionStorage.setItem('ugc_selectedSection', JSON.stringify({ Section: "All TOPICS", Sn: 0 }));
            setSelectedTopicsDropdown([]);
            setSelectedTopic({ Topic: "All SUB TOPICS", Sn: 0 });
            sessionStorage.setItem('ugc_selectedTopic', JSON.stringify({ Topic: "All SUB TOPICS", Sn: 0 }));
            return;
        }

        setSelectedSection(section);
        sessionStorage.setItem('ugc_selectedSection', JSON.stringify(section));
        let filteredTopics = topics.filter(topic => topic.SectionID === section.Sn);
        setSelectedTopicsDropdown(multiSortTopics(filteredTopics));
        setChecked([...multiSortTopics(filteredTopics)]);
        setSelectedTopic({ Topic: "All SUB TOPICS", Sn: 0 });
        sessionStorage.setItem('ugc_selectedTopic', JSON.stringify({ Topic: "All SUB TOPICS", Sn: 0 }));
    }

    const selectTopicHandler = (topic) => {
        if (!topic || topic.Topic === 'All SUB TOPICS') {
            let filteredTopics = topics.filter(t => t.SectionID === selectedSection.Sn);
            setChecked([...multiSortTopics(filteredTopics)]);
            setSelectedTopic({ Topic: "All SUB TOPICS" });
            sessionStorage.setItem('ugc_selectedTopic', JSON.stringify({ Topic: "All SUB TOPICS" }));
            return;
        }

        setSelectedTopic(topic);
        sessionStorage.setItem('ugc_selectedTopic', JSON.stringify(topic));
        setChecked([topic]);
    }

    // Helper functions to find entities for editing
    const findSubject = (subjectID) => subjects.find(s => s.Sn === subjectID);
    const findSection = (sectionID) => sections.find(s => s.Sn === sectionID);
    const findTopic = (topicID) => topics.find(t => t.Sn === topicID);

    return (
        <>
            <div className="row">
                <div className="col-xl-12">
                    <div className="row">
                        <div className="col-xl-12">
                            <div className=" page-title p-2 mb-2">
                                <div className="row w-100 d-flex justify-content-between">
                                    <div className='col-12 d-flex flex-wrap justify-content-start align-items-center gap-2'>
                                        <div className="col-12 col-md-auto d-flex flex-wrap gap-2 justify-content-start">
                                            <button type="button" className="btn btn-dark btn-xs" style={{ whiteSpace: 'nowrap', backgroundColor: '#274065', borderColor: '#274065' }}
                                                onClick={() => subjectRef.current.openModal({ ExamType: selectedExamType !== 'All Exam Types' ? selectedExamType : 'GENERAL APTITUDE' })}>+ SUBJECT NAME</button>
                                            <button type="button" className="btn btn-info btn-xs text-white" style={{ whiteSpace: 'nowrap', backgroundColor: '#00a99d', borderColor: '#00a99d' }}
                                                onClick={() => sectionRef.current.openModal({
                                                    ExamType: selectedExamType !== 'All Exam Types' ? selectedExamType : 'GENERAL APTITUDE',
                                                    Subject: selectedSubjectType.Subject !== 'All SUBJECT NAME' ? selectedSubjectType.Subject : '',
                                                    SubjectID: selectedSubjectType.Sn > 0 ? selectedSubjectType.Sn : 0
                                                })}>+ TOPICS</button>
                                            <button type="button" className="btn btn-primary btn-xs" style={{ whiteSpace: 'nowrap', backgroundColor: '#4d44b5', borderColor: '#4d44b5' }}
                                                onClick={() => topicRef.current.openModal({
                                                    ExamType: selectedExamType !== 'All Exam Types' ? selectedExamType : 'GENERAL APTITUDE',
                                                    Subject: selectedSubjectType.Subject !== 'All SUBJECT NAME' ? selectedSubjectType.Subject : '',
                                                    SubjectID: selectedSubjectType.Sn > 0 ? selectedSubjectType.Sn : 0,
                                                    Section: selectedSection.Section !== 'All TOPICS' ? selectedSection.Section : '',
                                                    SectionID: selectedSection.Sn > 0 ? selectedSection.Sn : 0
                                                })}>+ SUB TOPICS</button>
                                        </div>

                                        <div className="col-12 col-md-auto d-flex flex-wrap justify-content-start align-items-center gap-2">
                                            <div style={{ minWidth: '170px' }}>
                                                <Select
                                                    value={[
                                                        { value: 'All Exam Types', label: 'ALL SECTIONS' },
                                                        { value: 'GENERAL APTITUDE', label: 'Teaching & Research Aptitude' },
                                                        { value: 'SUBJECT SPECIFIC', label: 'Academic Subjects' },
                                                        { value: 'COMBINED', label: 'COMBINED TESTS' }
                                                    ].find(opt => opt.value === selectedExamType)}
                                                    onChange={(selectedOption) => selectExamTypeHandler(selectedOption.value)}
                                                    options={[
                                                        { value: 'All Exam Types', label: 'ALL SECTIONS' },
                                                        { value: 'GENERAL APTITUDE', label: 'Teaching & Research Aptitude' },
                                                        { value: 'SUBJECT SPECIFIC', label: 'Academic Subjects' },
                                                        { value: 'COMBINED', label: 'COMBINED TESTS' }
                                                    ]}
                                                    styles={{ control: (provided) => ({ ...provided, borderColor: '#17a2b8', minHeight: '35px', fontSize: '13px' }) }}
                                                />
                                            </div>
                                            <div className="col-12 col-sm-5 col-md-2" style={{ minWidth: '160px' }}>
                                                <Select
                                                    isDisabled={selectedExamType === 'All Exam Types'}
                                                    value={{ value: selectedSubjectType, label: selectedSubjectType.Subject }}
                                                    onChange={(selectedOption) => selectSubjectTypeHandler(selectedOption.value)}
                                                    options={[{ Sn: 0, Subject: 'All SUBJECT NAME' }, ...selectedSubjects]?.map((subject) => ({ value: subject, label: subject.Subject }))}
                                                    styles={{ control: (provided) => ({ ...provided, borderColor: '#17a2b8', minHeight: '35px', fontSize: '13px' }) }}
                                                />
                                            </div>
                                            <div className="col-12 col-sm-5 col-md-2" style={{ minWidth: '160px' }}>
                                                <Select
                                                    isDisabled={selectedSubjectType.Subject === 'All SUBJECT NAME'}
                                                    value={{ value: selectedSection, label: selectedSection.Section }}
                                                    onChange={(selectedOption) => selectSectionHandler(selectedOption.value)}
                                                    options={[{ Sn: 0, Section: 'All TOPICS' }, ...selectedSections]?.map((section) => ({ value: section, label: section.Section }))}
                                                    styles={{ control: (provided) => ({ ...provided, borderColor: '#17a2b8', minHeight: '35px', fontSize: '13px' }) }}
                                                />
                                            </div>
                                            <div className="col-12 col-sm-5 col-md-2" style={{ minWidth: '160px' }}>
                                                <Select
                                                    isDisabled={selectedSection.Section === 'All TOPICS'}
                                                    value={{ value: selectedTopic, label: selectedTopic.Topic }}
                                                    onChange={(selectedOption) => selectTopicHandler(selectedOption.value)}
                                                    options={[{ Sn: 0, Topic: 'All SUB TOPICS' }, ...selectedTopicsDropdown]?.map((topic) => ({ value: topic, label: topic.Topic }))}
                                                    styles={{ control: (provided) => ({ ...provided, borderColor: '#17a2b8', minHeight: '35px', fontSize: '13px' }) }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-12 wow fadeInUp" data-wow-delay="1.5s">
                            <div className="d-sm-flex text-center justify-content-between align-items-center">
                                <div className='dataTables_info text-dark'>
                                    Records: {checked.length}
                                </div>
                                {checked.length > pageSize && (
                                    <div className="dataTables_paginate paging_simple_numbers justify-content-center mb-0 pt-2">
                                        <Pagination count={number.length} variant="outlined" color='primary' size="small" page={currentPage} onChange={(e, p) => setCurrentPage(parseInt(p))} />
                                    </div>
                                )}
                            </div>

                            <div className="table-responsive full-data" style={{ paddingBottom: "1px", transform: 'rotateX(180deg)' }}>
                                <div id="example-student_wrapper" className="dataTables_wrapper no-footer">
                                    <table className="table-responsive-lg table display dataTablesCard student-tab dataTable no-footer mt-0 mb-0" style={{ transform: 'rotateX(180deg)' }}>
                                        <thead style={{ backgroundColor: "#cccccc" }} >
                                            <tr className='text-center border border-left'>
                                                <th className='custom-table-header p-2 text-center'>Sl.No.</th>
                                                <th className='custom-table-header p-2 text-center'>SECTIONS</th>
                                                <th className='custom-table-header p-2 text-center'>SUBJECT NAME</th>
                                                <th className='custom-table-header p-2 text-center'>TOPICS</th>
                                                <th className='custom-table-header p-2 text-center'>SUB TOPICS</th>
                                                <th className='custom-table-header p-2 text-center'>QUESTIONS COUNT</th>
                                                <th className='custom-table-header p-2 text-center'>ACTION</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {records.length > 0 ? records.map((item, ind) => (
                                                <tr key={ind} className={`${ind % 2 == 1 ? 'custom row-height' : 'row-height'}`} >
                                                    <td className='p-1 text-center' style={{ border: "1px solid #cccccc", width: "50px" }}>
                                                        <h6 className="mb-0 " style={{ fontSize: '12px' }}>
                                                            {item.displaySlNo}
                                                        </h6>
                                                    </td>
                                                    <td className='p-1 text-center' style={{ border: "1px solid #cccccc", width: "150px" }}>
                                                        <h6 className="mb-0 " style={{ fontSize: '12px' }}>
                                                            {getExamTypeLabel(item.ExamType)}
                                                        </h6>
                                                    </td>
                                                    <td className='p-1' style={{ border: "1px solid #cccccc", width: "180px" }}>
                                                        <div className="d-flex justify-content-between align-items-center px-2">
                                                            <h6 className="mb-0" style={{ fontSize: '13px', fontWeight: 'bold' }}>{item.Subject}
                                                                <CiEdit cursor='pointer' title='Edit SUBJECT NAME' size={18} className='text-info ms-3'
                                                                    onClick={() => subjectRef.current.openModal(findSubject(item.SubjectID))} />
                                                                <MdDeleteOutline color="red" title='Delete SUBJECT NAME' cursor='pointer' size={18} className='ms-2'
                                                                    onClick={() => deleteItem(item.SubjectID, 'SUBJECT NAME', '/ugc/deleteSubject/', setLoadData, loadData)} />
                                                            </h6>
                                                        </div>
                                                    </td>
                                                    <td className='p-1' style={{ border: "1px solid #cccccc", width: "180px" }}>
                                                        <div className="d-flex justify-content-between align-items-center px-2">
                                                            <h6 className="mb-0" style={{ fontSize: '13px', fontWeight: 'bold' }}>{item.Section}
                                                                <CiEdit cursor='pointer' title='Edit TOPICS' size={18} className='text-info ms-3'
                                                                    onClick={() => sectionRef.current.openModal(findSection(item.SectionID))} />
                                                                <MdDeleteOutline color="red" title='Delete TOPICS' cursor='pointer' size={18} className='ms-2'
                                                                    onClick={() => deleteItem(item.SectionID, 'TOPICS', '/ugc/deleteSection/', setLoadData, loadData)} />
                                                            </h6>
                                                        </div>
                                                    </td>
                                                    <td className='p-1' style={{ border: "1px solid #cccccc" }}>
                                                        <div className="d-flex justify-content-between align-items-center px-2">
                                                            <h6 className="mb-0" style={{ fontSize: '13px', fontWeight: 'bold' }}>{item.Topic}
                                                                <CiEdit cursor='pointer' title='Edit SUB TOPICS' size={18} className='text-info ms-3'
                                                                    onClick={() => topicRef.current.openModal(item)} />
                                                                <MdDeleteOutline color="red" title='Delete SUB TOPICS' cursor='pointer' size={18} className='ms-2'
                                                                    onClick={() => deleteItem(item.Sn, 'SUB TOPICS', '/ugc/deleteTopic/', setLoadData, loadData)} />
                                                            </h6>
                                                        </div>
                                                    </td>
                                                    <td className='p-1' style={{ border: "1px solid #cccccc", width: "180px" }}>
                                                        <div className="d-flex justify-content-center align-items-center gap-2 p-1">
                                                            <div className="d-flex align-items-center gap-1">
                                                                <span style={{ fontSize: '10px', fontWeight: '600' }}>Asigned:</span>
                                                                <span className="badge badge-success py-1 px-1" style={{ fontSize: '10px', minWidth: '25px' }}>{item.NoOfQuestion ?? 0}</span>
                                                            </div>
                                                            <div className="d-flex align-items-center gap-1">
                                                                <span style={{ fontSize: '10px', fontWeight: '600' }}>Available:</span>
                                                                <span className="badge badge-info py-1 px-1" style={{ fontSize: '10px', minWidth: '25px' }}>{item.AvailableQuestions ?? 0}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className='p-1 text-center' style={{ border: "1px solid #cccccc", width: "100px" }}>
                                                        <button type="button" className="btn btn-primary btn-xxs shadow"
                                                            onClick={() => questionModalRef.current.openModal({
                                                                examType: item.ExamType,
                                                                subject: item.Subject,
                                                                subjectId: item.SubjectID,
                                                                section: item.Section,
                                                                sectionId: item.SectionID,
                                                                topic: item.Topic,
                                                                topicId: item.Sn
                                                            })}
                                                        >Add Question</button>
                                                    </td>
                                                </tr>
                                            )) :
                                                <tr>
                                                    <td colSpan="7" className="text-center p-5">
                                                        {showTableResponse(topics, checked)}
                                                    </td>
                                                </tr>}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <UGC_SubjectModal ref={subjectRef} loadSubjects={loadData} setSubjectLoading={setLoadData} />
            <UGC_SectionModal ref={sectionRef} loadSubjects={loadData} setSubjectLoading={setLoadData} />
            <UGC_TopicModal ref={topicRef} loadSubjects={loadData} setSubjectLoading={setLoadData} />
            <UGC_QuestionBankModal ref={questionModalRef} loadQuestions={loadData} setLoadQuestions={setLoadData} setQuestionOffset={() => { }} />
        </>
    );
};

export default UGC_AllSubjects;
